import React, { useEffect, useState } from "react";
import { Calendar as CalendarIcon } from "@phosphor-icons/react";
import { CalendarPopover } from "./CalendarPopover";
import { useTranslation } from "react-i18next";

/**
 * v0.4.37 Q2 (operator sign-off 2026-06-04, defaults a/a/yes).
 *
 * Locale-independent DD/MM/YYYY date input.
 *
 * Why this exists:
 *   `<input type="date">` displays in the browser locale —
 *   MM/DD/YYYY on a US-locale browser, DD/MM/YYYY on en-GB,
 *   YYYY-MM-DD on some Linux setups. A UK VAT filing agent on a
 *   US-locale laptop seeing MM/DD/YYYY could silently enter the
 *   wrong period. Periods are compliance-critical inputs; the
 *   displayed format MUST match what the operator typed.
 *
 * Contract:
 *   - Display: DD/MM/YYYY, always.
 *   - Internal value passed via `onChange`: ISO `YYYY-MM-DD`
 *     string (backend payload format). Empty string while the
 *     user is mid-edit and the buffer hasn't parsed yet.
 *   - Invalid input: red ring on the input + `data-invalid="true"`
 *     attribute for tests; `onChange` is not called until the
 *     buffer parses cleanly.
 *
 * The component is intentionally tiny and dependency-free. No
 * date library needed — DD/MM/YYYY is a fixed 10-char shape we
 * can parse with one regex and `new Date()` round-trip.
 */

const DDMMYYYY_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const isoToDisplay = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const displayToIso = (display) => {
  const m = DDMMYYYY_RE.exec((display || "").trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = Number(dd), mo = Number(mm), y = Number(yyyy);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2999) return null;
  // round-trip through Date to catch e.g. 31/02/2026
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y
      || probe.getUTCMonth() !== mo - 1
      || probe.getUTCDate() !== d) return null;
  return `${yyyy}-${mm}-${dd}`;
};

export const DateInput = ({
  value, onChange, testid, className = "", "aria-label": ariaLabel,
  allowClear = false,
}) => {
  const { t } = useTranslation();
  // Buffer the literal characters the operator typed. Sync from
  // `value` whenever the parent passes a new ISO string in.
  const [buffer, setBuffer] = useState(() => isoToDisplay(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setBuffer(isoToDisplay(value));
    setInvalid(false);
  }, [value]);

  const handleChange = (e) => {
    const next = e.target.value;
    setBuffer(next);
    // v0.6.12 A5-27 — an OPTIONAL date (a registration's effective-from)
    // must be clearable. Without this, emptying the box left the old date
    // in the parent's state. Opt-in, so required dates (the filing period)
    // keep their behaviour exactly.
    if (allowClear && next.trim() === "") {
      setInvalid(false);
      if (value) onChange("");
      return;
    }
    const iso = displayToIso(next);
    if (iso) {
      setInvalid(false);
      if (iso !== value) onChange(iso);
    } else if (next.length === 10) {
      setInvalid(true);
    } else {
      // mid-edit, not enough chars yet — neither valid nor invalid
      setInvalid(false);
    }
  };

  const handleBlur = () => {
    if (!buffer) { setInvalid(false); return; }
    const iso = displayToIso(buffer);
    setInvalid(!iso);
  };

  // v0.4.88 — locale-safe calendar popover. Clicking the icon
  // toggles the calendar; picking a date emits ISO + closes.
  // Typing path is untouched.
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <div className="relative">
      <div className="flex items-stretch gap-0">
        <input
          type="text"
          inputMode="numeric"
          maxLength={10}
          placeholder="DD/MM/YYYY" /* i18n-ignore: date-format placeholder, not localized prose */
          value={buffer}
          onChange={handleChange}
          onFocus={() => setPopoverOpen(true)}
          onBlur={handleBlur}
          data-testid={testid}
          data-invalid={invalid ? "true" : "false"}
          aria-label={ariaLabel}
          className={`${className} ${invalid ? "ring-1 ring-red-500" : ""} pr-9 cursor-pointer`}
        />
        <button
          type="button"
          data-testid={testid ? `${testid}-calendar-toggle` : "calendar-toggle"}
          onClick={() => setPopoverOpen((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
          aria-label={t("ui.calendar.open", { defaultValue: "Open calendar" })}
        >
          <CalendarIcon size={14} weight="bold" />
        </button>
      </div>
      {popoverOpen && (
        <CalendarPopover
          isoValue={value}
          onPick={(iso) => {
            setBuffer(isoToDisplay(iso));
            setInvalid(false);
            if (iso !== value) onChange(iso);
            setPopoverOpen(false);
          }}
          onClose={() => setPopoverOpen(false)}
          testidPrefix={testid ? `${testid}-cal` : "cal"}
        />
      )}
    </div>
  );
};

// Exported for direct test access (locked into source by the Q2
// behavioural test).
export const __testHelpers = { isoToDisplay, displayToIso };
